import { IPlanSeries } from './planSeries';


export type TStorageStrategyUnlimited = 'data_size' | 'space_capacity';

/** 套餐状态 */
export enum EPlanStatus {

    /** 套餐可以续费可以新购 */
    ENABLED='enabled',

    /** 套餐禁止续费禁止新购 */
    DISABLED='disabled',

    /** 套餐可以续费不可新购 */
    RENEW_ONLY='renew_only'
}

export interface ISalePriceBreakdown {

    /**
     * 流量分摊售价
     *
     * 取值范围: 非负数字符串，精确到小数点后两位
     */
    data: string;

    /**
     * 云存储分摊售价
     *
     * 取值范围: 非负数字符串，精确到小数点后两位
     */
    storage: string;
}

export interface ICostBreakdown {

    /**
     * 套餐的基础费用，如果套餐没有基础费用，则为 '0.00'
     *
     * 取值范围: 非负数字符串，精确到小数点后两位
     */
    basicFee: string;
}

export interface IPlan {

    /**
     * 套餐 id
     *
     * 取值范围: uint53 字符串
     */
    id: string;

    /**
     * 套餐系列 key
     *
     * 长度:1-64 个字符
     */
    seriesKey: string;

    /** 系列的信息，当前plan对应的planSeries下不存在套餐时，products接口不会返回 */
    series?: IPlanSeries;

    /** plan标题，实际为series.i18n.name, 需手动补充*/
    title?: string;

    /**
     * 可以平滑切换到的套餐
     *
     * 取值范围: uint64 字符串
     */
    portablePlan: string;

    /**
     * 套餐使用的币种
     *
     * @deprecated
     *
     * 请使用 pricing 字段中的货币字段
     *
     * 取值范围: USD/CAD/GBP/EUR/CNY 等三个大写字母组成的货币代号
     */
    currency: string;

    /**
     * 支付周期
     *
     * @deprecated 该字段已经废弃，不再使用。请使用 {@link pricing} 字段
     */
    cycle: {

        /**
         * 每个支付周期的原价
         *
         * 取值范围: 非负数字符串，精确到小数点后两位
         */
        regularPrice: string;

        /**
         * 每个支付周期的现价
         *
         * 取值范围: 非负数字符串，精确到小数点后两位
         */
        salePrice: string;

        /**
         * 每个支付周期的积分
         *
         * 取值范围: 非负数字符串，精确到小数点后两位
         */
        credits?: string;

        /**
         * 每个支付周期间隔时长
         *
         * @deprecated 该字段已经废弃，不再使用。请使用 {@link duration} 字段
         */
        interval: {

            /**
             * 每个支付周期间隔时长
             *
             * 取值范围: safe_uint
             *
             * 如果单位是天，则不得小于 3
             */
            frequency: number;

            /**
             * 支付周期单位
             *
             * - 'day'   套餐支付周期为 天
             * - 'week'  套餐支付周期为 周
             * - 'month' 套餐支付周期为 月
             * - 'year'  套餐支付周期为 年
             */
            unit: 'day' | 'week' | 'month' | 'year';
        };

    };

    /**
     * 价格参数，当前套餐仅支持一种货币
     *
     */
    pricing: {

        /**
         * 套餐使用的币种
         *
         * 取值范围: USD/CAD/GBP/EUR/CNY 等三个大写字母组成的货币代号
         */
        currency: string;

        /**
         * 原价
         *
         * 取值范围: 非负数字符串，精确到小数点后两位
         */
        regularPrice: string;

        /**
         * 售价
         *
         * 取值范围: 非负数字符串，精确到小数点后两位
         */
        salePrice: string;

        /**
         * 每次每个套餐充值的积分，如果不支持积分，则为 '0.00'
         *
         * 取值范围: 非负数字符串，精确到小数点后两位
         */
        credits: string;

        /**
         * 售价明细，明细中每一项的和需要和 salePrice 相等
         *
         */
        salePriceBreakdown: ISalePriceBreakdown;

        /**
         * 套餐成本明细，明细中每一项的和必需小于等于 salePrice
         *
         * 取值范围: 非负数字符串，精确到小数点后两位
         */
        costBreakdown: ICostBreakdown;
    };

    /**
     * 默认自动续费间隔时长，不支持自动续费可以不传。当前默认就是单个周期套餐的有效时长
     *
     */
    defaultAutoRenewalInterval?: ITemporalObj;

    /**
     * 单个套餐的有效时长
     *
     */
    duration: ITemporalObj;

    /**
     * 套餐状态
     *
     * - 'enabled'   套餐可以续费可以新购
     * - 'disabled'  套餐禁止续费禁止新购
     * - 'renew_only' 套餐可以续费不可新购
     */
    status: EPlanStatus;

    /**
     * 套餐的存储策略
     *
     * 取值范围: 1-64 个字符
     */
    storageStrategy: string;

    /**
     * 套餐限制条件
     */
    limitations: {

        /**
         * 最多有多少订阅能使用当前套餐(每个用户)
         */
        userMaxSubscriptionQty: number;

        /**
         * 该套餐最多可以购买几次(每个用户)
         */
        userMaxSubscribeTimes: number;

        /**
         * 单个订阅每次最多可以买多少个周期（每个订阅）
         */
        subscriptionMaxPurchaseCycles: number;

        /**
         * 是否可以从其他套餐切换到当前套餐
         */
        switchFromOther: boolean;

        /**
         * 单个设备最多可以订阅多少个周期（每台设备）
         * 值为 0 时该限制条件不生效
         *
         * @default 0
         */
        deviceMaxSubscribeCycles?: number;
        
        /**
         * 单个 SIM 卡最多可以订阅多少个周期（每张 SIM 卡）
         * 值为 0 时该限制条件不生效
         * 取值范围: int(0,255) 
         * @default 0
         */
        simMaxSubscribeCycles?: number;

    };

    /**
     * 前台显示参数
     */
    style: {

        'plan.sort': number;
        'cloud_storage.ai_group_id'?: string;
        'cloud_storage.allow_ai'?: boolean;
        'cloud_storage.allow_img'?: boolean;
        'cloud_storage.allow_rich_notification'?: boolean;
        'cloud_storage.rich_notification'?: 'disabled' | 'static_image_only' | 'static_and_dynamic_image';
        'cloud_storage.allow_speed_play'?: boolean;
        'cloud_storage.allowed_video_types'?: string[];
        'cloud_storage.space_capacity'?: number;
        'cloud_storage.data_size'?: number;
        'cloud_storage.max_device_quantity'?: number;
        'cloud_storage.video_lifetime'?: number;
        'cloud_storage.default_retention_day'?: number;
        'cloud_storage.unlimited'?: TStorageStrategyUnlimited[];
        /** 每日带图推送张数上限；0 或未配置视为无限，>0 为有限额 */
        'rich_notification.daily_limit'?: number;
        [x: string]: any;
    };

    /**
     * i18n 语言包参数
     */
    i18n: {
        description: string;
        [x: string]: string;
    };

    /**
     * 存储国家信息, 注意只有使用getIPlan才会自动带上，不然需要自己手动添加
     */
    storageRegion?: string;
    /**
     * 设备支持列表信息, 注意只有使用getIPlan才会自动带上，不然需要自己手动添加
     */
    productSupportedString?: string;
    
    /**
     * 套餐是否可以被免费领取
     * 
     * @default false
     */
    freeOfferAvailable?: boolean;

}

export interface IGetPlanByIdRes {
    plan: IPlan;
    series: IPlanSeries;
}

export interface ITemporalObj {

    /**
     * 数目
     *
     * 取值范围: safe_uint
     */
    amount: number;

    /**
     * 周期单位
     *
     * - 'day'   套餐支付周期为 天
     * - 'week'  套餐支付周期为 周
     * - 'month' 套餐支付周期为 月
     * - 'year'  套餐支付周期为 年
     */
    unit: 'day' | 'week' | 'month' | 'year';
}

export type IProductPlan = IPlan & {
    title: string;
    countryGroups: string[];
};

export interface IGetPlanBlocklistArgs {

    /**
     * 设备UID
     *
     * 取值范围: string(16)
     */
    uid?: string;

    /**
     * SIM卡ICCID
     * 
     * 取值范围: string(20)
     */
    iccid?: string;
}

export interface IGetPlanBlocklistResponse {

    /**
     * 套餐ID限制列表
     */
    planIds: string[];
}